import React, { useEffect, useState } from 'react';
import { IconButton, Menu, MenuItem } from '@klover/attain-design-system';
import { nanoid } from 'nanoid';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useUser } from 'reactfire';
// Material UI Components
import Box from '@material-ui/core/Box';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import {
  ClockCounterClockwise,
  DotsThreeVertical,
  FloppyDiskBack,
  PencilSimple,
  X,
} from '@phosphor-icons/react';
import {
  Theme,
  ThemeProvider,
  createStyles,
  createTheme,
  makeStyles,
} from '@material-ui/core/styles';

// Components
import AudienceDeleteDialog from '../../pages/audienceManager/audienceDeleteDialog';

import BootstrapTooltip from 'components/bootstrapTooltip';
// import ExportDialog from '../exportDialog';
import ExportHistroryDialog from '../../pages/audienceManager/exportHistoryDialog';
import tokens from 'tokens';
import {
  AUDIENCE_HISTORY,
  AUDIENCE_NAME,
  AUDIENCE_NAME_LENGTH_VALIDATION_MESSAGE,
  EDIT_AUDIENCE_NAME,
  ENTER_AUDIENCE_NAME_TO_CONTINUE,
  SAVE_AUDIENCE_TITLE,
  WAIT_FOR_AUDIENCE_LOAD,
} from '../../content';
import {
  AUDIENCE_LAL_MODAL_TYPE,
  CREATION_APPLICATION,
  ENUM_SYSTEM_USED,
  LoadingStates,
} from '../../constants';
import {
  addNotification,
  createCombinedAudience,
  updateCombinedAudience,
  updateCurrentAudience,
} from '../../pages/audienceManager/slice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    audienceName: {
      fontFamily: 'Calibre-Semibold, sans-serif',
      fontSize: 24,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      width: 400,
      marginRight: 10,
    },
    groupName: {
      fontFamily: 'Calibre-Regular,sans-serif',
      fontSize: 16,
    },
    editAudienceName: {
      fontFamily: 'Calibre-Regular,sans-serif',
      width: 400,
    },
    button: {
      margin: `${theme.spacing(0, 1)} !important`,
    },
    exportSpinner: {
      margin: '0.15em 1.2em',
    },
    editButtonBox: {
      marginLeft: theme.spacing(4),
    },
    form: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
    },
    menuItem: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 5,
    },
    audienceNameContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      marginTop: 1,
    },
    saveBtn: {
      color: 'red',
      fontSize: '13px',
    },
  })
);

const inputs = createTheme({
  overrides: {
    MuiFormControl: {
      root: {
        maxHeight: 48,
      },
    },
    MuiFormLabel: {
      root: {
        maxHeight: 48,
      },
    },
    MuiInputLabel: {
      root: {
        marginTop: -5,

        '&$focused': {
          marginTop: 0,
        },
      },
    },
    MuiInputBase: {
      root: {
        maxHeight: 48,
      },
      input: {
        borderRadius: 10,
        alignSelf: 'center',
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10,
      },
      input: {
        padding: '12px 14px',
      },
    },
    MuiButton: {
      root: {
        borderRadius: 10,
      },
      outlinedPrimary: {
        color: tokens.colorButtonPrimary,
      },
      containedPrimary: {
        backgroundColor: tokens.colorButtonPrimary,
        '&:hover': {
          backgroundColor: tokens.colorButtonPrimary,
        },
      },
    },
    MuiButtonBase: {
      root: {
        borderRadius: 10,
      },
    },
  },
});

interface AudienceNameProps {
  isAudienceLoading: boolean;
  isAudienceCreated: boolean;
  name: string;
  setName: any;
  selectedAudiences: any;
  operatorType: string;
}

// we need to know when audience is loading to determine if we should disable some text fields and buttons
const CombineAudienceName: React.FC<AudienceNameProps> = ({
  isAudienceLoading,
  isAudienceCreated,
  name,
  setName,
  selectedAudiences,
  operatorType,
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [editMode, setEditMode] = useState(false);

  const [notChanging, setNotChanging] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openExportHistoryDialog, setOpenExportHistoryDialog] = useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { data: user } = useUser();
  const currentUser = user || { email: '' };

  const currentGroup = useAppSelector((state) => {
    return state.audienceReducer.currentGroup;
  });

  const currentAudience = useAppSelector((state) => {
    return state.audienceReducer.currentAudience;
  });
  const openAudienceAction = Boolean(anchorEl);
  const isError = name?.length > 175 || false;

  useEffect(() => {
    setName(currentAudience?.name || '');
    const isNameBlank = currentAudience?.name
      ? currentAudience.name === ''
      : true;
    setEditMode(isNameBlank);
  }, [currentAudience]);

  const handleCancel = () => {
    setEditMode(false);
    if (currentAudience) {
      setName(currentAudience?.name);
    }
  };

  const handleEditName = () => {
    setEditMode(true);
  };

  const handleNameChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const name = event.target.value as string;
    setName(name);
  };

  const handleSave = () => {
    if (currentAudience) {
      if (selectedAudiences.length < 2) {
        dispatch(
          addNotification({
            state: LoadingStates.ERROR,
            message: 'Please select at least two audiences.',
          })
        );
      } else if (selectedAudiences.length > 10) {
        dispatch(
          addNotification({
            state: LoadingStates.ERROR,
            message: 'You can select up to 10 audiences only.',
          })
        );
      } else {
        const updatedAudience = {
          ...currentAudience,
          name: name?.trim(),
          created_by_email: currentUser?.email,
          updated_by_email: currentUser?.email,
          audiences: selectedAudiences,
          group_id: currentGroup?.id,
          operator: operatorType,
          audience_type: ENUM_SYSTEM_USED.COMBINED_AUDIENCE,
          creation_application: CREATION_APPLICATION.KDS,
          lal_model_type: AUDIENCE_LAL_MODAL_TYPE.ATTAIN,
          combinedAudienceId: currentAudience?.combined_audience_id,
          showLoader: true,
        };

        const audienceExists = currentAudience.name;
        if (audienceExists) {
          dispatch(updateCombinedAudience(updatedAudience));
        } else {
          dispatch(createCombinedAudience(updatedAudience));
        }
        dispatch(updateCurrentAudience(updatedAudience));
      }
    }
  };

  return (
    <>
      <ThemeProvider theme={inputs}>
        {editMode ? (
          <Box className={classes.form}>
            <div className={classes.audienceNameContainer}>
              <TextField
                disabled={isAudienceLoading}
                key={currentAudience?.name && notChanging ? nanoid() : null}
                onFocus={() => setNotChanging(false)}
                id="audience-name"
                label={AUDIENCE_NAME}
                onChange={handleNameChange}
                defaultValue={currentAudience?.name}
                value={name}
                variant="outlined"
                style={{ marginRight: 8 }}
                className={classes.editAudienceName}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <BootstrapTooltip
                        title={
                          !name?.trim()
                            ? ENTER_AUDIENCE_NAME_TO_CONTINUE
                            : SAVE_AUDIENCE_TITLE
                        }
                      >
                        <span>
                          <IconButton
                            color="default"
                            disabled={
                              name?.trim().length === 0 ||
                              isError ||
                              isAudienceLoading
                            }
                            onClick={handleSave}
                            size="small"
                          >
                            <FloppyDiskBack size={24} />
                          </IconButton>
                        </span>
                      </BootstrapTooltip>
                    </InputAdornment>
                  ),
                }}
              />
              {isError && (
                <span className={classes.saveBtn}>
                  {AUDIENCE_NAME_LENGTH_VALIDATION_MESSAGE}
                </span>
              )}
            </div>
            {isAudienceCreated && (
              <BootstrapTooltip title={'Exit Save Audience'}>
                <span>
                  <IconButton
                    disabled={isAudienceLoading}
                    onClick={handleCancel}
                  >
                    <X size={24} />
                  </IconButton>
                </span>
              </BootstrapTooltip>
            )}
          </Box>
        ) : (
          <>
            <Box flexDirection="column">
              <BootstrapTooltip title={currentAudience?.name}>
                <Typography
                  className={classes.audienceName}
                  component="h2"
                  variant="h6"
                >
                  {currentAudience?.name}
                </Typography>
              </BootstrapTooltip>

              <Typography
                component="h3"
                variant="body2"
                className={classes.groupName}
              >
                {currentAudience?.group_name}
              </Typography>
            </Box>
            <BootstrapTooltip
              title={
                isAudienceLoading ? WAIT_FOR_AUDIENCE_LOAD : EDIT_AUDIENCE_NAME
              }
            >
              <IconButton
                color="default"
                disabled={isAudienceLoading}
                onClick={handleEditName}
              >
                <PencilSimple size={24} />
              </IconButton>
            </BootstrapTooltip>
            <BootstrapTooltip title="Actions" placement="top">
              <span aria-describedby="audience-action-btn">
                <IconButton
                  color="default"
                  disabled={isAudienceLoading}
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <DotsThreeVertical size={24} weight="bold" />
                </IconButton>

                <Menu
                  id="audience-action-btn"
                  open={openAudienceAction}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  onBlur={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={() => setOpenExportHistoryDialog(true)}>
                    <span
                      title="Show Audience History"
                      className={classes.menuItem}
                    >
                      <ClockCounterClockwise size={22} />
                      {AUDIENCE_HISTORY}
                    </span>
                  </MenuItem>
                </Menu>
              </span>
            </BootstrapTooltip>
          </>
        )}
      </ThemeProvider>
      <AudienceDeleteDialog
        isOpen={openDeleteDialog}
        setOpenDialog={setOpenDeleteDialog}
        user={currentUser.email}
      />
      <ExportHistroryDialog
        isOpen={openExportHistoryDialog}
        setOpenDialog={setOpenExportHistoryDialog}
        data={currentAudience?.exportHistory || []}
        normalAudience={currentAudience}
      />
    </>
  );
};

export default CombineAudienceName;
